import { GridItem } from '../../components/common/grid/GridItem'
import { t } from 'i18next'
import { Title } from '../../components/common/texts/Title'
import { Button, makeStyles, Menu, MenuItem, MenuList, MenuPopover, MenuTrigger, tokens } from '@fluentui/react-components'
import { Add16Regular, MoreVertical20Regular } from '@fluentui/react-icons'
import { ControlledTextInput } from '../../components/common/inputs/TextInput'
import { ControlledPersonalNumberInput } from './ControlledPersonalNumberInput'
import { useEffect, useState } from 'react'
import { useFieldArray, UseFormReturn } from 'react-hook-form'
import { enumValuesToOptions } from '../../helpers/enumHelper'
import { OrganisationWithUserAccountsDTO, Role } from '../../api/schemas/schema'
import { ControlledDropDown } from '../../components/common/inputs/Dropdown'
import { IModalState } from '../../interfaces/IModalState'
import { ControlledPhoneInput } from '../../components/common/inputs/ControlledPhoneInput'
import { validateSystemadminRequirement } from '../../helpers/organisationHelper'
import { ModalWarningConfirm } from './ModalUncheckWarning'
import { Grid } from '../../components/common/grid/Grid'

type UserAccountFormProps = {
   organisationForm: UseFormReturn<OrganisationWithUserAccountsDTO, any, undefined>
   modalState: IModalState<OrganisationWithUserAccountsDTO>
}
const useStyles = makeStyles({
   userButton: {
      marginRight: tokens.spacingHorizontalXS,
   },
   wrap: {
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      gap: tokens.spacingHorizontalL,
   },
   bg: {
      backgroundColor: tokens.colorNeutralBackground3,
      padding: tokens.spacingHorizontalM,
      borderRadius: tokens.borderRadiusXLarge,
   },
   container: {
      marginTop: tokens.spacingVerticalXXL,
      '@media (max-width: 1007px)': {
         display: 'flex',
         flexDirection: 'column',
      },
   },
})
export const UserAccountForm = ({ organisationForm, modalState }: UserAccountFormProps) => {
   const classes = useStyles()
   const [selectedUserIndex, setSelectedUserIndex] = useState<number | null>(modalState?.item?.userAccounts?.length > 0 ? 0 : null)
   const [showWarning, setShowWarning] = useState(false)
   const [resolveDelete, setResolveDelete] = useState<(result: boolean) => void>()
   const {
      control,
      watch,
      setError,
      clearErrors,
      formState: { errors },
   } = organisationForm
   const { fields, append, remove } = useFieldArray({
      control: control,
      name: 'userAccounts',
   })
   const handleAddUser = () => {
      const defaultUserAccount = { firstName: '', lastName: '', email: '', phoneNumber: '' }
      append(defaultUserAccount)

      const users = watch('userAccounts')
      handleSelectUser(users.length - 1)
   }

   const handleDeleteUser = (index: number) => {
      remove(index)

      const users = watch('userAccounts')
      if (users.length === 0) {
         setSelectedUserIndex(null)
         return
      } else if (selectedUserIndex === index) {
         setSelectedUserIndex(0)
      } else if (index < selectedUserIndex) {
         setSelectedUserIndex(selectedUserIndex - 1)
      }
   }

   const handleSelectUser = (index: number) => {
      setSelectedUserIndex(index)
   }

   const deleteWarning = () => {
      return new Promise<boolean>((resolve) => {
         setShowWarning(true)
         setResolveDelete(() => resolve)
      })
   }
   const confirmDelete = async (confirmed: boolean) => {
      setShowWarning(false)
      resolveDelete?.(confirmed)
   }

   useEffect(() => {
      validateSystemadminRequirement(watch('userAccounts'), setError, clearErrors)
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [JSON.stringify(watch('userAccounts'))])

   return (
      <Grid className={classes.container}>
         <GridItem size={'12/12'}>
            <Title as="h5">{t('User')}</Title>
         </GridItem>
         <GridItem size={'12/12'}>
            <Button size="medium" icon={<Add16Regular fontSize={20} />} appearance="transparent" onClick={handleAddUser}>
               {t('AddUser')}
            </Button>
         </GridItem>
         <GridItem size={'12/12'} className={classes.wrap}>
            {fields.map((field, index) => (
               <div key={field.id} className={classes.bg}>
                  <Button
                     className={classes.userButton}
                     key={field.id}
                     size="medium"
                     appearance={selectedUserIndex === index ? 'primary' : 'secondary'}
                     onClick={() => handleSelectUser(index)}
                  >
                     {watch(`userAccounts.${index}.firstName`) !== '' || watch(`userAccounts.${index}.lastName`) !== ''
                        ? `${watch(`userAccounts.${index}.firstName`) ?? ''} ${watch(`userAccounts.${index}.lastName`) ?? ''}`
                        : `${t('User')} ${index + 1}`}
                  </Button>
                  <Menu>
                     <MenuTrigger>
                        <Button icon={<MoreVertical20Regular />} appearance="transparent" onClick={(e: any) => e.stopPropagation()} />
                     </MenuTrigger>
                     <MenuPopover>
                        <MenuList>
                           <MenuItem
                              onClick={async (e) => {
                                 e.stopPropagation()
                                 const confirmed = await deleteWarning()
                                 if (confirmed) handleDeleteUser(index)
                              }}
                           >
                              {t('Delete')}
                           </MenuItem>
                        </MenuList>
                     </MenuPopover>
                  </Menu>
               </div>
            ))}
         </GridItem>
         {selectedUserIndex !== null && (
            <>
               <GridItem size="6/12">
                  <ControlledDropDown
                     errorMessage={errors?.userAccounts?.message}
                     key={`role-${selectedUserIndex}`}
                     name={`userAccounts.${selectedUserIndex}.role`}
                     control={control}
                     label={t('Roles')}
                     options={enumValuesToOptions(Role)}
                     required
                  />
               </GridItem>
               <GridItem size={'6/12'}>
                  <ControlledPersonalNumberInput
                     required
                     name={`userAccounts.${selectedUserIndex}.user.personalNumber`}
                     label={t('PersonalNumber')}
                     control={control}
                     watch={watch}
                     key={selectedUserIndex}
                  />
               </GridItem>
               <GridItem size={'6/12'}>
                  <ControlledTextInput
                     required
                     name={`userAccounts.${selectedUserIndex}.firstName`}
                     label={t('FirstName')}
                     control={control}
                     key={selectedUserIndex}
                  />
               </GridItem>
               <GridItem size={'6/12'}>
                  <ControlledTextInput
                     required
                     name={`userAccounts.${selectedUserIndex}.lastName`}
                     label={t('LastName')}
                     control={control}
                     key={selectedUserIndex}
                  />
               </GridItem>
               <GridItem size={'6/12'}>
                  <ControlledTextInput
                     required
                     name={`userAccounts.${selectedUserIndex}.email`}
                     label={t('Email')}
                     control={control}
                     type="email"
                     key={selectedUserIndex}
                  />
               </GridItem>
               <GridItem size={'6/12'}>
                  <ControlledPhoneInput
                     name={`userAccounts.${selectedUserIndex}.phoneNumber`}
                     label={t('PhoneNumber')}
                     control={control}
                     key={selectedUserIndex}
                  />
               </GridItem>
            </>
         )}
         <ModalWarningConfirm
            open={showWarning}
            title={t('ConfirmDeleteUser')}
            message={t('ConfirmDeleteUserMsg')}
            confirmButtonText={t('DeleteUser')}
            confirmFunction={confirmDelete}
         />
      </Grid>
   )
}
